import { isFailedToFetchError } from '../../api/api.retry';
import type {
  AsyncOperation,
  AsyncOperationClientState,
  AsyncOperationSummary,
  CreateOperationScopeSnapshotInput,
  OperationKey,
  OperationReference,
  OperationScope,
} from './asyncOperations.types';

class AsyncOperationRequestError extends Error {
  readonly name = 'AsyncOperationRequestError';
  readonly code: string | undefined;
  readonly retryable: boolean | undefined;

  constructor(
    readonly httpStatus: number,
    readonly apiError: unknown
  ) {
    super(
      apiError &&
        typeof apiError === 'object' &&
        'message' in apiError &&
        typeof apiError.message === 'string'
        ? apiError.message
        : `Operation request failed with status ${httpStatus}`
    );
    this.code =
      apiError &&
      typeof apiError === 'object' &&
      'code' in apiError &&
      typeof apiError.code === 'string'
        ? apiError.code
        : undefined;
    this.retryable = isRetryableApiError(apiError) || undefined;
  }
}

const canonicalizeRegionalApiBaseUrl = (value: string) => {
  let url: URL;

  try {
    url = new URL(value);
  } catch {
    throw new Error('Regional operation scope requires a valid API base URL');
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error(
      'Regional operation scope requires an HTTP(S) API base URL'
    );
  }

  url.hash = '';
  url.search = '';

  return url.toString().replace(/\/$/, '');
};

const isRetryableApiError = (error: unknown) =>
  !!error &&
  typeof error === 'object' &&
  'retryable' in error &&
  error.retryable === true;

export const createOperationScopeSnapshot = ({
  apiTarget,
  dataRegion,
  regionalApiBaseUrl,
  requesterTeamId,
}: CreateOperationScopeSnapshotInput): OperationScope => {
  if (!requesterTeamId) {
    throw new Error('Operation scope requires a requester team ID');
  }

  if (apiTarget === 'regional') {
    if (!dataRegion) {
      throw new Error('Regional operation scope requires a data region');
    }
    if (!regionalApiBaseUrl) {
      throw new Error('Regional operation scope requires an API base URL');
    }

    return Object.freeze({
      requesterTeamId,
      placement: Object.freeze({
        apiTarget,
        dataRegion,
        regionalApiBaseUrl: canonicalizeRegionalApiBaseUrl(regionalApiBaseUrl),
      }),
    });
  }

  if (regionalApiBaseUrl) {
    throw new Error(
      'Control-plane operation scope cannot include a regional API base URL'
    );
  }

  return Object.freeze({
    requesterTeamId,
    placement: Object.freeze({
      apiTarget,
      dataRegion: dataRegion ?? null,
    }),
  });
};

export const getOperationScopeKey = (scope: OperationScope) =>
  JSON.stringify([
    scope.requesterTeamId,
    scope.placement.apiTarget,
    scope.placement.dataRegion,
    scope.placement.apiTarget === 'regional'
      ? scope.placement.regionalApiBaseUrl
      : null,
  ]);

export const getOperationKey = (reference: OperationReference): OperationKey =>
  JSON.stringify([
    reference.operationId,
    reference.requesterTeamId,
    reference.placement.apiTarget,
    reference.placement.dataRegion,
    reference.placement.apiTarget === 'regional'
      ? reference.placement.regionalApiBaseUrl
      : null,
  ]) as OperationKey;

export const createAsyncOperationRequestError = (
  httpStatus: number,
  apiError: unknown
) => new AsyncOperationRequestError(httpStatus, apiError);

export const getAsyncOperationErrorStatus = (error: unknown) =>
  error instanceof AsyncOperationRequestError ? error.httpStatus : undefined;

export const isAsyncOperationNotFoundError = (error: unknown) => {
  const status = getAsyncOperationErrorStatus(error);

  return status === 404 || status === 410;
};

export const isTransientAsyncOperationError = (error: unknown) => {
  if (isFailedToFetchError(error)) return true;
  if (!(error instanceof AsyncOperationRequestError)) return false;

  return (
    error.httpStatus === 429 ||
    error.httpStatus >= 500 ||
    isRetryableApiError(error.apiError)
  );
};

export const isTerminalOperation = (
  operation: Pick<AsyncOperation, 'status'>
) => operation.status === 'completed' || operation.status === 'failed';

export const getPrimaryOperationResource = (
  operation: Pick<AsyncOperationSummary, 'resource'>
) => operation.resource;

export const getResultOperationResource = (
  operation: Pick<AsyncOperation, 'resource' | 'resources'>
) =>
  operation.resources.find((resource) => resource.role === 'result') ??
  (operation.resource?.role === 'result' ? operation.resource : null);

export const getNewestOperationForResource = (
  operations: readonly AsyncOperationSummary[],
  resource: NonNullable<AsyncOperationSummary['resource']>,
  operationType?: AsyncOperationSummary['type']
) =>
  operations
    .filter(
      (operation) =>
        operation.resource?.id === resource.id &&
        operation.resource.type === resource.type &&
        (!operationType || operation.type === operationType)
    )
    .reduce<AsyncOperationSummary | undefined>((newest, operation) => {
      if (
        !newest ||
        Date.parse(operation.createdAt) > Date.parse(newest.createdAt)
      ) {
        return operation;
      }

      return newest;
    }, undefined);

export const getAsyncOperationClientState = ({
  data,
  enabled,
  error,
  isPending,
}: {
  data: AsyncOperation | undefined;
  enabled: boolean;
  error: unknown;
  isPending: boolean;
}): AsyncOperationClientState => {
  if (!enabled) return 'idle';
  if (isAsyncOperationNotFoundError(error)) return 'notFound';
  if (error && !isTransientAsyncOperationError(error)) return 'error';
  if (data) return data.status;
  if (isPending || error) return 'loading';

  return 'idle';
};
